<script>
import ProspectCampaignStatusIndicators from '@components/prospects/prospect-campaign-status-indicators.vue';
import formatPhoneNumber from '@utils/format-phone-number';
import { mixin as clickaway } from 'vue-clickaway';
import { prospectMethods, alertMethods, clientMethods } from '@state/helpers';

export default {
  components: {
    ProspectCampaignStatusIndicators,
  },
  props: {
    prospect: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  mixins: [clickaway],
  data() {
    return {
      showProspectOpts: false,
    };
  },
  computed: {
    preferredConnectingOption() {
      const phone = this.prospect.connectionOptions?.find(
        (option) => option.type === 'phone'
      );
      return phone?.value
        ? formatPhoneNumber(phone?.value)
        : this.prospect.connectionOptions?.find(
            (option) => option.type === 'email'
          )?.value;
    },
    hasUnreadProspectMessages() {
      return this.prospect?.unreadCount > 0;
    },
    hasValidPhoneConnOpts() {
      const phoneConnOpts = this.prospect.connectionOptions.find(
        (contact) => contact.type === 'phone'
      );
      return phoneConnOpts?.value;
    },
    hasValidEmailConnOpts() {
      const emailConnOpts = this.prospect.connectionOptions.find(
        (contact) => contact.type === 'email'
      );
      return emailConnOpts?.value;
    },
  },
  methods: {
    ...alertMethods,
    ...prospectMethods,
    ...clientMethods,
    prospectFullName(prospect) {
      return `${prospect.firstName} ${prospect.lastName}`;
    },
    toProspectDetail() {
      this.setSelectedProspect(this.prospect);
      this.$router
        .push({
          name: 'prospectProfile',
          params: { id: this.prospect._id, prospect: this.prospect },
        })
        .catch(() => {});
    },
    composeMsg(type) {
      this.setSelectedProspect(this.prospect);
      this.$emit('compose-single-msg', type);
    },
    async saveAsClient() {
      this.setProspectConfirmationContext({
        prospect: this.prospect,
      });
      this.$bvModal.show('prospect-confirmation-modal');
    },

    removeProspect() {
      this.deleteProspect(this.prospect._id);
    },
    async readMessages() {
      await this.setSelectedProspect(this.prospect);
      this.$router
        .push({
          name: 'prospectMessages',
          params: { id: this.prospect._id, prospect: this.prospect },
        })
        .catch(() => {});
    },
    toggleShowProspectOpts() {
      this.showProspectOpts = !this.showProspectOpts;
    },
  },
};
</script>

<template>
  <div
    :class="['card-prospect', { 'options-visible': showProspectOpts }]"
    @click="toProspectDetail"
    v-if="prospect"
  >
    <div class="avatar"></div>
    <div class="card-content">
      <div class="contact-details">
        <h5 class="prospect-name">{{ prospectFullName(prospect) }} </h5>
        <p v-if="preferredConnectingOption">{{ preferredConnectingOption }}</p>
      </div>

      <ProspectCampaignStatusIndicators :prospect="prospect" />
    </div>
    <div
      :class="['card-actions', { 'msg-indicator': hasUnreadProspectMessages }]"
    >
      <span
        class="unread-msg-count-wrapper"
        @click.stop="toggleShowProspectOpts"
      >
        <span class="unread-msg-count" v-if="hasUnreadProspectMessages">{{
          prospect.unreadCount
        }}</span>
        <BaseIcon name="ellipsis-h" />
      </span>
    </div>
    <div
      class="card-options"
      v-if="showProspectOpts"
      v-on-clickaway="toggleShowProspectOpts"
    >
      <ul>
        <li
          @click.stop="composeMsg('sms')"
          :class="{ disabled: !hasValidPhoneConnOpts }"
          >Send SMS</li
        >
        <li
          @click.stop="composeMsg('email')"
          :class="{ disabled: !hasValidEmailConnOpts }"
          >Send Email</li
        >
        <li @click.stop="saveAsClient">Save as Client</li>
        <li @click.stop="removeProspect">Delete Lead</li>
        <li class="read-msg-link" @click.stop="readMessages"
          >Read Messages ({{ prospect.unreadCount }})</li
        >
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.card-prospect {
  align-items: center;
  background-color: white;
  border: 1px solid $color-light-gray-four;
  border-radius: $base-border-radius-md;
  display: flex;
  height: 8.8rem;
  margin: 0 0.8rem 0.8rem 0;
  padding: 0.8rem;
  position: relative;
  transition: transform $duration-animation-base ease-out,
    box-shadow $duration-animation-base ease-out;
  width: 68.8rem;

  &:hover {
    box-shadow: 0 0.2rem 0.5rem rgba($color-black-wo-rgb, 0.1);
    cursor: pointer;
    transform: scale(1.01);
  }

  &.options-visible {
    transform: unset;
    transition: transform 1ms;
  }

  .prospect-name {
    color: $color-heading-text;
    margin-bottom: 0;
  }

  .avatar {
    align-items: center;
    background-color: $color-light-gray-five;
    border-radius: $base-border-radius-md;
    display: flex;
    height: 7.2rem;
    justify-content: center;
    margin-right: 1.3rem;
    width: 7.2rem;

    .icon-plus {
      font-size: 1.4rem;
    }
  }

  .card-content {
    align-items: center;
    color: $color-heading-text;
    display: flex;
    flex: 1.5;
    font-size: 1.8rem;
    font-weight: 800;
    letter-spacing: -0.257143px;
    line-height: 2.4rem;

    p {
      color: $color-gray-one;
      margin-bottom: 0;
    }

    .contact-details {
      flex: 1;
    }
  }

  .card-actions {
    align-items: center;
    display: flex;
    position: relative;
    right: 1.6rem;

    .fa-ellipsis-h {
      color: black;
      font-size: 1.8rem;

      &:hover {
        cursor: pointer;
      }
    }
    &.msg-indicator {
      align-items: center;
      background-color: $color-red;
      border-radius: $base-border-radius-lg;
      display: flex;
      height: 2.4rem;
      padding: 0 1rem;
      right: 0.6rem;

      .fa-ellipsis-h,
      .unread-msg-count {
        color: white;
      }

      .unread-msg-count-wrapper {
        align-items: center;
        display: flex;
        justify-content: center;
      }

      .unread-msg-count {
        font-size: 1.4rem;
        font-weight: 900;
        letter-spacing: -0.257143px;
        line-height: 1.3rem;
        margin-right: 1rem;
      }
    }
  }

  .card-options {
    background: white;
    border-radius: $base-border-radius-md;
    box-shadow: 0 0 0.4rem rgba($color-black-wo-rgb, 0.15);
    padding: 1.8rem 1.8rem 0;
    position: absolute;
    right: 0.6rem;
    top: 5.7rem;
    z-index: 1;

    &:hover {
      cursor: default;
    }

    li {
      color: black;
      flex-grow: 0;
      font-size: 1.4rem;
      font-weight: 300;
      letter-spacing: -0.257143px;
      line-height: 2.4rem;
      margin: 0.6rem 0;
      order: 1;

      &:hover {
        cursor: pointer;
      }

      &.read-msg-link {
        color: $color-red;
        font-weight: bold;
      }
      &.disabled {
        opacity: 0.5;
        pointer-events: none;
      }
    }
  }
  &.archived-overlay {
    &::after {
      background: rgba($color-white-wo-rgb, 0.5);
      border-radius: $base-border-radius-md;
      bottom: 0;
      content: '';
      left: 0;
      position: absolute;
      right: 0;
      top: 0;
    }
  }
}
</style>
